import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DollarSign, Save, History, ChevronDown, ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  dtf: "DTF Printing",
  screen: "Screen Printing",
  embroidery: "Embroidery",
  garments: "Garment Costs",
  stickers: "Stickers",
  fees: "Fees & Services",
};

const PricingAdmin = () => {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [historyData, setHistoryData] = useState({});

  const fetchRules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pricing`, { withCredentials: true });
      setRules(data.rules || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error("Failed to load pricing rules", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setEditValue(rule.value.toString());
  };

  const saveEdit = async (ruleId) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/pricing/${ruleId}`, { value: parseFloat(editValue) }, { withCredentials: true });
      setEditingId(null);
      fetchRules();
    } catch (err) {
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = async (ruleId) => {
    if (expanded[ruleId]) {
      setExpanded(prev => ({ ...prev, [ruleId]: false }));
      return;
    }
    try {
      const { data } = await axios.get(`${API}/pricing/${ruleId}/history`, { withCredentials: true });
      setHistoryData(prev => ({ ...prev, [ruleId]: data.history || [] }));
      setExpanded(prev => ({ ...prev, [ruleId]: true }));
    } catch { /* ignore history load failure */ }
  };

  const rulesByCategory = categories.reduce((acc, cat) => {
    acc[cat] = rules.filter(r => r.category === cat);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading pricing rules...</div>;

  return (
    <div className="space-y-4" data-testid="pricing-admin">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" /> Pricing Engine
        </h2>
        <Badge className="bg-green-900/30 text-green-400 text-xs">{rules.length} rules</Badge>
      </div>

      {categories.map(cat => (
        <Card key={cat} className="bg-zinc-900 border-zinc-800" data-testid={`pricing-cat-${cat}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">{CATEGORY_LABELS[cat] || cat}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {(rulesByCategory[cat] || []).map(rule => (
                <div key={rule.id} data-testid={`rule-${rule.key}`}>
                  <div className="flex items-center justify-between bg-zinc-800 rounded-lg p-3">
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium">{rule.name}</p>
                      <p className="text-[10px] text-zinc-500">{rule.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {editingId === rule.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 h-7 text-xs bg-zinc-700 border-zinc-600 text-white"
                            data-testid={`edit-input-${rule.key}`}
                            onKeyDown={(e) => e.key === "Enter" && saveEdit(rule.id)}
                          />
                          <Button size="sm" onClick={() => saveEdit(rule.id)} disabled={saving} className="h-7 text-xs bg-green-600 hover:bg-green-700" data-testid={`save-${rule.key}`}>
                            <Save className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 text-xs text-zinc-400">
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-mono font-bold text-green-400">{rule.unit === "multiplier" ? `${rule.value}x` : `$${rule.value.toFixed(2)}`}</span>
                          <span className="text-[10px] text-zinc-600">{rule.unit}</span>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(rule)} className="h-7 text-xs text-zinc-400 hover:text-white" data-testid={`edit-${rule.key}`}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggleHistory(rule.id)} className="h-7 w-7 p-0 text-zinc-500" data-testid={`history-${rule.key}`}>
                            {expanded[rule.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <History className="w-3.5 h-3.5" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {expanded[rule.id] && historyData[rule.id] && (
                    <div className="ml-4 mt-1 mb-2 pl-3 border-l-2 border-zinc-700 space-y-1">
                      {historyData[rule.id].length === 0 ? (
                        <p className="text-[10px] text-zinc-600">No changes recorded yet</p>
                      ) : (
                        historyData[rule.id].map((h) => (
                          <div key={h.timestamp} className="text-[10px] text-zinc-500 flex items-center gap-2">
                            <span>{new Date(h.timestamp).toLocaleString()}</span>
                            {Object.entries(h.changes).map(([field, change]) => (
                              <span key={field}>
                                {field}: <span className="text-red-400 line-through">{typeof change.from === "number" ? `$${change.from}` : change.from}</span>
                                {" "}<span className="text-green-400">{typeof change.to === "number" ? `$${change.to}` : change.to}</span>
                              </span>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default PricingAdmin;
